import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import SubscriptionModal from "./subscriptionModal";
import PaymentMethodModal from "./paymentMethodModal";

function PricingSubscriptions() {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const [loading, setLoading] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [formState, setFormState] = useState({
    SubscriptionPlan: "ServeIQ for Restaurant Free",
    LocationCount: 1,
    KioskCount: 0,
    SubscriptionEndDate: "29 March 2025",
    SubscriptionPlanName: "ServeIQ for Restaurant Plus",
  });

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${APIs.GET_COMPANY_SETTINGS}`);
      const data = response.data.data;
      setFormState((prev) => ({
        ...prev,
        SubscriptionPlan:
          data.SubscriptionPlan || "ServeIQ for Restaurant Free",
        LocationCount: data.LocationCount || 1,
        KioskCount: data.KioskCount || 0,
        SubscriptionEndDate: data.SubscriptionEndDate || "29 March 2025",
        SubscriptionPlanName:
          data.SubscriptionPlanName || "ServeIQ for Restaurant Plus",
      }));
    } catch (err) {
      showErrorToast("Failed to load settings");

    } finally {
      setLoading(false);
    }
  }, [CompID]);

  const fetchPaymentMethods = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${APIs.GET_PAYMENT_METHODS}`);
      setPaymentMethods(response.data.data || []);
    } catch (err) {
      showErrorToast("Failed to load payment methods");

    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchPaymentMethods();
  }, [fetchSettings, fetchPaymentMethods]);

  const handlePaymentMethodSubmit = async (paymentData, isUpdate) => {
    try {
      setLoading(true);
      const url = isUpdate
        ? `${APIs.UPDATE_PAYMENT_METHOD}`
        : `${APIs.ADD_PAYMENT_METHOD}`;
      const method = isUpdate ? "patch" : "post";

      const response = await axiosInstance[method](url, paymentData);

      if (response.data.status) {
        showSuccessToast(
          response.data.Message ||
            `Payment method ${isUpdate ? "updated" : "added"} successfully`
        );

        if (isUpdate) {
          setPaymentMethods((prev) =>
            prev.map((pm) =>
              pm.id === paymentData.ID ? { ...pm, ...paymentData } : pm
            )
          );
        } else {
          setPaymentMethods((prev) => [...prev, paymentData]);
        }
      } else {
        showErrorToast(
          response.data.Message ||
            `Failed to ${isUpdate ? "update" : "add"} payment method`
        );
      }
    } catch (error) {
      showErrorToast(
        error.response?.data?.Message ||
          `Error ${isUpdate ? "updating" : "adding"} payment method`
      );

    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionSubmit = async (paymentMethod) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.SUBSCRIBE_PLUS_PLAN, {
        compID: CompID,
        subscriptionDate: new Date().toISOString(),
        paymentMethod: paymentMethod,
      });

      if (response.data.status) {
        showSuccessToast(
          response.data.Message || "Successfully subscribed to Plus plan"
        );
      } else {
        showErrorToast(
          response.data.Message || "Failed to subscribe to Plus plan"
        );
      }
    } catch (error) {
      showErrorToast(
        error.response?.data?.Message || "Error subscribing to Plus plan"
      );

    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="container-fluid px-4 py-4">
        <div className="d-flex align-items-center mb-3">
          <div>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="#/">Home</a>
              </li>
              <li className="breadcrumb-item active">Pricing & Subscriptions</li>
            </ol>
            {/* <h1 className="page-header mb-0">Pricing & Subscriptions</h1> */}
          </div>
        </div>

        <GridSectionLoader loading={loading} minHeight="280px">
        <div className="row">
          <div className="col-xl-12 col-lg-12 col-md-12">
            <div className="mb-4 pb-3">
              <h4 className="d-flex align-items-center mb-2">
                Pricing & Subscriptions
              </h4>
              <p>Manage your pricing and subscription settings.</p>
              <div className="card">
                <div className="list-group list-group-flush fw-bold mt-3">
                  <div className="list-group-item d-flex align-items-center">
                    <div className="flex-fill">
                      <div className="d-flex align-items-center">
                        <i className="fa-solid fa-credit-card"></i>
                        <span className="m-2">Credit Card</span>
                      </div>
                      <div className="text-muted mt-1">
                        {paymentMethods.length > 0
                          ? `${paymentMethods.length} payment method${paymentMethods.length > 1 ? "s" : ""} configured`
                          : "No payment method configured"}
                      </div>
                    </div>
                    <div>
                      <button
                        className="btn btn-link text-primary p-0"
                        onClick={() => setIsPaymentModalOpen(true)}
                      >
                        Add payment method
                      </button>
                    </div>
                  </div>
                </div>

                <div className="m-4">
                  <h5>Subscriptions</h5>
                  <div className="list-group list-group-flush fw-bold">
                    <div className="list-group-item d-flex align-items-center">
                      <div className="flex-fill">
                        <div>{formState.SubscriptionPlan}</div>
                        <div className="text-muted mt-1">
                          You have {formState.LocationCount} location
                        </div>
                        <div className="text-muted mt-1">
                          You have {formState.KioskCount} kiosk devices
                        </div>
                        <div className="text-muted mt-1">
                          {formState.SubscriptionPlanName} ended{" "}
                          {formState.SubscriptionEndDate}
                        </div>
                      </div>
                      <div>
                        <button
                          className="btn btn-link text-primary p-0"
                          onClick={() => setIsSubscriptionModalOpen(true)}
                        >
                          Upgrade to Plus plan
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </GridSectionLoader>
      </div>

      <PaymentMethodModal
        isModalOpen={isPaymentModalOpen}
        setIsModalOpen={setIsPaymentModalOpen}
        onPaymentMethodSubmit={handlePaymentMethodSubmit}
        compID={CompID}
      />

      <SubscriptionModal
        isModalOpen={isSubscriptionModalOpen}
        setIsModalOpen={setIsSubscriptionModalOpen}
        onSubscriptionSubmit={handleSubscriptionSubmit}
        compID={CompID}
      />
    </>
  );
}

export default PricingSubscriptions;